import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewDmNotification, updateDmNotificationAssignee } from '../services/dmNotifications';
import { useToast } from '../providers/ToastProvider';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiError';
import { DM_MESSAGES_INBOX_KEY, DM_NOTIFICATIONS_KEY } from './queryKeys';
import { invalidateSidebarCounts } from './useSidebarCounts';

const HTTP_NOT_FOUND = 404;

export type DmNotificationReviewOutcome =
    'reviewed' | 'activationEmailSent' | 'alreadyResolvedInDm' | 'gone';

const REVIEW_SUCCESS_TOAST: Partial<Record<DmNotificationReviewOutcome, string>> = {
    reviewed: 'Marked as reviewed',
    activationEmailSent: 'Activation email sent',
    alreadyResolvedInDm: 'Already reviewed in DM, removed'
};

interface AssignDmNotificationPatientParams {
    patientId: string;
    email: string | null;
}

export function useAssignDmNotificationPatient() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ patientId, email }: AssignDmNotificationPatientParams) =>
            updateDmNotificationAssignee(patientId, email),
        // The messages list shows the same assignee, so both lists refresh.
        onSettled: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: [DM_NOTIFICATIONS_KEY] }),
                queryClient.invalidateQueries({ queryKey: [DM_MESSAGES_INBOX_KEY] }),
                invalidateSidebarCounts(queryClient)
            ])
    });
}

// Toasts live here rather than in the caller: the row unmounts once the refetch drops it.
interface ReviewDmNotificationOptions {
    // Awaited before the refetch, so the list can't drop the row mid exit animation.
    onReviewed?: (notificationId: string) => Promise<void>;
    sendsActivationEmail?: boolean;
}

interface ReviewDmNotificationVariables {
    notificationId: string;
    // Omitted means the backend's default (send), same as checking the dialog's box.
    sendActivation?: boolean;
}

export function useReviewDmNotification({
    onReviewed,
    sendsActivationEmail = false
}: ReviewDmNotificationOptions = {}) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation({
        mutationFn: async ({
            notificationId,
            sendActivation
        }: ReviewDmNotificationVariables): Promise<DmNotificationReviewOutcome> => {
            try {
                const result = await reviewDmNotification(notificationId, sendActivation);
                if (result.alreadyResolvedInDm) return 'alreadyResolvedInDm';
                return sendsActivationEmail && sendActivation !== false
                    ? 'activationEmailSent'
                    : 'reviewed';
            } catch (error) {
                // 404: someone else already cleared it, which is the outcome the user wanted.
                if (apiErrorStatus(error) === HTTP_NOT_FOUND) return 'gone';
                throw error;
            }
        },
        onSuccess: async (outcome, variables) => {
            const message = REVIEW_SUCCESS_TOAST[outcome];
            if (message) showToast(message, 'success');
            await onReviewed?.(variables.notificationId);
        },
        onError: (error) => {
            showToast(
                apiErrorMessage(error, 'Failed to mark the notification as reviewed'),
                'error'
            );
        },
        onSettled: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: [DM_NOTIFICATIONS_KEY] }),
                invalidateSidebarCounts(queryClient)
            ])
    });
}
